import { useEffect } from 'react';

/**
 * Small imperative SEO helper: sets document.title, meta description,
 * canonical URL, OG/Twitter tags, and optionally injects a JSON-LD block
 * tagged with a data-seo attribute so it can be swapped on route change.
 */
export function useSeo({ title, description, canonical, image, type = 'article', jsonLd, keywords }) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (attr, key, value) => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };

    setMeta('name', 'description', description);
    if (keywords) setMeta('name', 'keywords', keywords);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    if (canonical) setMeta('property', 'og:url', canonical);
    if (image) setMeta('property', 'og:image', image);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    if (image) setMeta('name', 'twitter:image', image);

    // Canonical link
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // JSON-LD block per-route
    const tagName = 'data-seo-jsonld';
    const previous = document.head.querySelector(`script[${tagName}]`);
    if (previous) previous.remove();
    if (jsonLd) {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute(tagName, 'true');
      s.text = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }

    return () => {
      const s = document.head.querySelector(`script[${tagName}]`);
      if (s) s.remove();
    };
  }, [title, description, canonical, image, type, keywords, jsonLd]);
}
